import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'

import { getCurrentUserGroup, getFilteredGroups, getOtherStoresGroups } from './helpers'

const useUserDetail = ({ storeId, userId, selectedGroupId }) => {
  const { data: userInfo, loading: loadingUser } = useFetch(() => configurationServices.getUser({ storeId, userId }), {
    doInitialRequest: userId !== 'new',
  })

  const { fetchData: createUser, loading: creatingUser } = useFetch(configurationServices.createUser, {
    doInitialRequest: false,
  })

  const { fetchData: editUser, loading: editingUser } = useFetch(configurationServices.editUser, {
    doInitialRequest: false,
  })

  const { data: userGroups, loading: loadingGroups } = useFetch(
    () => configurationServices.getUserGroups({ storeId }),
    {
      doInitialRequest: true,
    }
  )

  const { fetchData: deleteUser, loading: deletingUser } = useFetch(configurationServices.deleteUser, {
    doInitialRequest: false,
  })

  const [image, setImage] = useState({ file: null, preview: userInfo?.profile_picture_url || null })
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [group, setGroup] = useState('')
  const [availableGroups, setAvailableGroups] = useState([])
  const [otherStoresGroups, setOtherStoresGroups] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [errorField, setErrorField] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const setInitialValues = useCallback(() => {
    if (userInfo || userId === 'new') {
      setImage({ file: null, preview: userInfo?.profile_picture_url || null })
      setName(userInfo?.first_name || '')
      setLastName(userInfo?.last_name || '')
      setUsername(userInfo?.username || '')
      setEmail(userInfo?.email || '')
      setErrorField('')
      setErrorMessage('')
      setHasChanges(false)
    }
  }, [userInfo, userId])

  useEffect(() => {
    setInitialValues()
  }, [userInfo, setInitialValues])

  useEffect(() => {
    if (userGroups) {
      setAvailableGroups(getFilteredGroups(userGroups, userInfo?.is_owner))
      setGroup(getCurrentUserGroup(userGroups, userId) || selectedGroupId)
      setOtherStoresGroups(getOtherStoresGroups(userInfo, storeId))
    }
  }, [userGroups, userInfo, userId, storeId, selectedGroupId])

  const deleteImage = () => {
    setImage({
      preview: null,
      file: new File([], ''),
    })
    setHasChanges(true)
  }

  const changeImage = file => {
    setImage({
      preview: URL.createObjectURL(file),
      file,
    })
    setHasChanges(true)
  }

  const revertChanges = () => {
    setInitialValues()
  }

  return {
    name,
    setName: param => {
      setHasChanges(true)
      setName(param)
    },
    lastName,
    setLastName: param => {
      setHasChanges(true)
      setLastName(param)
    },
    username,
    setUsername: param => {
      setHasChanges(true)
      setUsername(param)
    },
    email,
    setEmail: param => {
      setHasChanges(true)
      setEmail(param)
    },
    image,
    group,
    setGroup: param => {
      setHasChanges(true)
      setGroup(param)
    },
    deleteImage,
    changeImage,
    isOwner: userInfo?.is_owner,
    hasChanges,
    setHasChanges,
    revertChanges,
    loadingUser,
    createUser,
    creatingUser,
    editUser,
    editingUser,
    availableGroups,
    loadingGroups,
    deleteUser,
    deletingUser,
    otherStoresGroups,
    errorField,
    setErrorField,
    errorMessage,
    setErrorMessage,
  }
}

export default useUserDetail
